// Find-in-trace for the trace detail's Context reading. A hit is one
// case-insensitive occurrence of the term in the text a block renders when it
// carries marks, so every hit counted has a mark to land on. The block views
// in ContextPanel render through the same helpers below for that reason.

import { formatConversationValue } from "./conversationContent.js";
import { toolUseText } from "./signalHighlight.js";
import { toolResultText } from "./traceToolCall.js";
import { parseCellEnvelope } from "./traceCodeModeCells.js";
import { codexAgentMessagesIn } from "./traceAgentSpawns.js";

export const MIN_TERM = 2;
export const MAX_HITS = 1000;
export const SEARCH_KINDS = Object.freeze(["user", "assistant", "tool", "system"]);
export const SEARCH_MARK_TYPE = "search.hit";

export function toolUseBodyText(block) {
  if (block?.input == null) return "";
  return block.typedInput ? formatConversationValue(block.typedInput) : toolUseText(block.input);
}

// `env` is the code-mode cell envelope when the result carries one; the body
// is then the program's own output.
export function toolResultReading(block) {
  const text = block?.typedContent && typeof block.content !== "string"
    ? formatConversationValue(block.typedContent)
    : toolResultText(block?.content);
  const env = parseCellEnvelope(text);
  return { env, body: env ? env.text : text };
}

// Description then schema: the exact tool_defs surface a signal scans.
export function toolDefText(block) {
  const parts = [];
  if (typeof block?.description === "string" && block.description) parts.push(block.description);
  if (block?.schema != null) {
    try {
      parts.push(block.typedSchema ? formatConversationValue(block.typedSchema) : typeof block.schema === "string" ? block.schema : JSON.stringify(block.schema, null, 2));
    } catch { /* unserializable schema - skip */ }
  }
  return parts.join("\n");
}

export function blockSearchText(block) {
  switch (block?.type) {
    case "text": return typeof block.text === "string" ? block.text : null;
    case "thinking": return typeof block.thinking === "string" ? block.thinking : null;
    case "tool_use": return block.input != null ? toolUseBodyText(block) : null;
    case "tool_result": return toolResultReading(block).body || null;
    case "tool_def": return toolDefText(block) || null;
    default: return null;
  }
}

// Envelopes a harness wraps around text it sends in the user's role: Claude
// Code reminders, notifications and local-command echoes; IDE context; Codex
// session context and scheduled-automation heartbeats. Slash-command stamps
// (<command-name>) and `!` input are what the person typed, so they are not
// listed.
const HARNESS_TAG_SPAN = /<(system-reminder|task-notification|local-command-[a-z]+|bash-stdout|bash-stderr|ide_selection|ide_opened_file|environment_context|user_instructions|turn_aborted|heartbeat)\b[^>]*>[\s\S]*?(?:<\/\1>|$)/gi;
// Harness text that arrives without an envelope, matched at the block's start:
// notices and markers, compaction summaries, and the prompts Claude Code writes
// for its own side requests (web fetch and search, session titles, status
// lines, next-prompt suggestions), which share the session with the person's
// turns. Case-sensitive: harnesses write these exactly, and folding case would
// catch prompts like "[Notifications] add an unread badge".
const HARNESS_PREFIX = new RegExp("^(?:" + [
  String.raw`\[[^\]\n]*(?:NOTIFICATION|NOT USER INPUT|AUTOMATED|SUGGESTION MODE)[^\]\n]*\]`,
  String.raw`\[Request interrupted by user[^\]\n]*\]\s*$`,
  // A workflow's computed task; its relayed "user request" sibling is the
  // person's words and stays theirs.
  String.raw`\[Workflow harness\W+computed task\]`,
  "Another Claude session sent a message",
  "Base directory for this skill:",
  "Caveat: The messages below were generated by the user while running local commands",
  "Goal check-in:",
  // Claude Code's request for a recap when the person comes back to a session.
  "The user stepped away and is coming back.",
  "This session is being continued from a previous conversation",
  "Web page content:",
  "Perform a web search for the query:",
  String.raw`Describe your most recent action in \d+-\d+ words`,
  String.raw`<session>[\s\S]*</session>\s*Write the title`,
].join("|") + ")");

// True when a user-role text block holds only harness-added text, or another
// agent's message (a Codex agent frame). A block that mixes a reminder with
// words the person typed stays theirs.
export function isHarnessText(text) {
  if (typeof text !== "string") return false;
  if (codexAgentMessagesIn(text).length) return true;
  const rest = text.replace(/^\uFEFF/, "").replace(HARNESS_TAG_SPAN, "").trim();
  return rest === "" ? text.trim() !== "" : HARNESS_PREFIX.test(rest);
}

// Claude Code follows a ToolSearch result (tool_reference blocks) with this
// note in the same message; on its own the words could be typed.
function isToolLoadedNote(turn, text) {
  return typeof text === "string" && /^Tools? loaded\.$/.test(text.trim())
    && Array.isArray(turn?.blocks) && turn.blocks.some((b) => b?.type === "tool_result");
}

// Block type decides before role: tool results ride user-role messages in the
// Anthropic shape, and a reader filtering to "User" means what a person typed,
// so harness text sent in the user's role counts as system.
export function blockKind(turn, block) {
  const type = block?.type;
  if (type === "tool_use" || type === "tool_result") return "tool";
  if (type === "tool_def") return "system";
  if (type !== "text" && type !== "thinking") return null;
  const role = turn?.role;
  if (role === "user" && type === "text" && (isHarnessText(block.text) || isToolLoadedNote(turn, block.text))) return "system";
  return role === "user" || role === "assistant" || role === "system" ? role : null;
}

// The blocks of `blocks` a type filter keeps, as [block, index] pairs; every
// block when no kind is chosen. Indices stay the turn's own so marks still land.
export function blocksOfKinds(turn, blocks, kinds) {
  const pairs = (blocks || []).map((block, i) => [block, i]);
  if (!Array.isArray(kinds) || !kinds.length) return pairs;
  const wanted = new Set(kinds);
  return pairs.filter(([block]) => wanted.has(blockKind(turn, block)));
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const defaultBlocks = (turn) => (Array.isArray(turn?.blocks) ? turn.blocks : []);

// A case-insensitive regex rather than lowercasing both sides: lowercasing can
// change a string's length, which would shift every offset after it.
function termPattern(term) {
  return new RegExp(escapeRegExp(term), "giu");
}

export function matchRanges(text, term) {
  if (typeof text !== "string" || typeof term !== "string" || term.length < MIN_TERM) return [];
  const re = termPattern(term);
  const out = [];
  let m;
  while ((m = re.exec(text)) !== null) out.push([m.index, m.index + m[0].length]);
  return out;
}

// Returns null without a term, else { term, hits: [{ id, turn, block, start, end, match }], capped }.
// `kinds` narrows which blocks are searched. `blocksOf` must return the blocks
// as the view renders them, since hit coordinates are (turn, block) indices
// into that list. `id` survives earlier pages being prepended when the turn
// carries a message id.
export function searchTurns(turns, { term = "", kinds = [], blocksOf = defaultBlocks } = {}) {
  if (!Array.isArray(turns) || typeof term !== "string" || term.length < MIN_TERM) return null;
  const kindSet = Array.isArray(kinds) && kinds.length ? new Set(kinds) : null;
  const re = termPattern(term);
  const hits = [];
  for (let ti = 0; ti < turns.length; ti++) {
    const turn = turns[ti];
    const blocks = blocksOf(turn);
    const turnKey = turn?.messageId ?? `t${ti}`;
    for (let bi = 0; bi < blocks.length; bi++) {
      const block = blocks[bi];
      if (kindSet && !kindSet.has(blockKind(turn, block))) continue;
      const text = blockSearchText(block);
      if (!text) continue;
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(text)) !== null) {
        if (hits.length >= MAX_HITS) return { term, hits, capped: true };
        hits.push({ id: `${turnKey}:${bi}:${m.index}`, turn: ti, block: bi, start: m.index, end: m.index + m[0].length, match: m[0] });
      }
    }
  }
  return { term, hits, capped: false };
}

// Indices of the messages holding at least one block of the given kinds, in
// order; null when no kind is chosen (every message shows).
export function turnsOfKinds(turns, kinds, blocksOf = defaultBlocks) {
  if (!Array.isArray(kinds) || !kinds.length) return null;
  const wanted = new Set(kinds);
  const out = [];
  (turns || []).forEach((turn, i) => {
    if (blocksOf(turn).some((block) => wanted.has(blockKind(turn, block)))) out.push(i);
  });
  return out;
}

// Loaded messages holding at least one block of each kind, for the filter's counts.
export function kindCounts(turns, blocksOf = defaultBlocks) {
  const counts = Object.fromEntries(SEARCH_KINDS.map((k) => [k, 0]));
  for (const turn of turns || []) {
    const seen = new Set();
    for (const block of blocksOf(turn)) {
      const kind = blockKind(turn, block);
      if (kind && !seen.has(kind)) {
        seen.add(kind);
        counts[kind] += 1;
      }
    }
  }
  return counts;
}

// The highlight-map shape ContextPanel's block views read, keyed "turn:block".
export function searchHighlightMap(hits) {
  const map = new Map();
  for (const h of hits || []) {
    const key = `${h.turn}:${h.block}`;
    const arr = map.get(key) || [];
    arr.push({ match: h.match, type: SEARCH_MARK_TYPE, start: h.start, end: h.end, hit: h.id });
    map.set(key, arr);
  }
  return map;
}

// Hits in the order the reader steps through them: down the screen. Within a
// message, blocks always render first to last; only the message order flips.
export function hitsInDisplayOrder(hits, historyOrder) {
  if (historyOrder !== "latest") return hits;
  return [...hits].sort((a, b) => b.turn - a.turn || a.block - b.block || a.start - b.start);
}
